import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Linkedin, Mail, MessageCircle, PhoneCall } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { contactDetails, rateCardUrl } from "@/data/eventOrganizerContent";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  full_name: "",
  email: "",
  phone: "",
  event_type: "",
  primary_market: "",
  event_date: "",
  budget: "",
  guest_count: "",
  preferred_contact: "Email",
  message: "",
};

const ConsultationSection = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedEventDate = formData.event_date ? new Date(`${formData.event_date}T00:00:00`) : undefined;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      primary_market: formData.primary_market || null,
      guest_count: formData.guest_count ? Number(formData.guest_count) : null,
      event_date: formData.event_date || null,
      budget: formData.budget || null,
    };

    try {
      await axios.post(`${API_URL}/api/consultations`, payload);
      toast.success("Consultation request sent. We will contact you within 24 hours.");
      setFormData(initialFormData);
    } catch (error) {
      toast.error("Could not submit your request. Please try WhatsApp or phone.");
      console.error("Consultation submission failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateSelect = (dateValue) => {
    setFormData((previous) => ({
      ...previous,
      event_date: dateValue ? format(dateValue, "yyyy-MM-dd") : "",
    }));
  };

  return (
    <section id="consultation" data-testid="consultation-section" className="py-24">
      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <Card data-testid="consultation-contact-card" className="border-violet-900 bg-violet-950 text-stone-100">
          <CardContent className="space-y-7 p-7">
            <div className="space-y-3">
              <p
                data-testid="consultation-eyebrow-text"
                className="inline-flex rounded-full border border-fuchsia-300/50 bg-fuchsia-100/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-fuchsia-200"
              >
                Corporate Consultation Desk
              </p>
              <h2 data-testid="consultation-heading" className="text-3xl leading-tight text-stone-50 md:text-5xl">
                Book your event strategy consultation.
              </h2>
              <p data-testid="consultation-subheading-text" className="text-sm text-stone-300 md:text-base">
                Discuss end-to-end management, speaker acquisition, content generation, marketing, emcee support,
                and conference production with our Pan-Asia team based in Hong Kong.
              </p>
            </div>

            <div className="grid gap-2">
              <p data-testid="consultation-offerings-heading" className="text-xs uppercase tracking-[0.14em] text-fuchsia-200">
                Popular requests
              </p>
              <div className="flex flex-wrap gap-2">
                <span data-testid="consultation-offering-end-to-end" className="rounded-full bg-white/10 px-3 py-1 text-xs text-stone-100">End-to-End Management</span>
                <span data-testid="consultation-offering-content" className="rounded-full bg-white/10 px-3 py-1 text-xs text-stone-100">Content Generation</span>
                <span data-testid="consultation-offering-conference-speaker" className="rounded-full bg-white/10 px-3 py-1 text-xs text-stone-100">Conference + Speaker Acquisition</span>
                <span data-testid="consultation-offering-marketing" className="rounded-full bg-white/10 px-3 py-1 text-xs text-stone-100">Event Marketing</span>
                <span data-testid="consultation-offering-emcee" className="rounded-full bg-white/10 px-3 py-1 text-xs text-stone-100">Emcee Services</span>
              </div>
            </div>

            <Button
              asChild
              variant="outline"
              className="h-10 w-full rounded-full border-fuchsia-200/70 bg-white/10 text-xs font-semibold uppercase tracking-[0.13em] text-fuchsia-100 hover:bg-white/20"
              data-testid="consultation-rate-card-link-button"
            >
              <a href={rateCardUrl} target="_blank" rel="noreferrer" aria-label="Download corporate rate card">
                Download Rate Card
              </a>
            </Button>

            <div className="space-y-3">
              <Button
                asChild
                className="h-11 w-full justify-start rounded-full bg-white/10 text-stone-100 hover:bg-white/20"
                data-testid="consultation-phone-button"
              >
                <a href={contactDetails.phoneHref} aria-label="Call event organizer">
                  <PhoneCall className="h-4 w-4" />
                  {contactDetails.phoneLabel}
                </a>
              </Button>
              <Button
                asChild
                className="h-11 w-full justify-start rounded-full bg-fuchsia-600 text-white hover:bg-fuchsia-500"
                data-testid="consultation-whatsapp-button"
              >
                <a href={contactDetails.whatsappHref} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp">
                  <MessageCircle className="h-4 w-4" />
                  Chat on WhatsApp
                </a>
              </Button>
              <Button
                asChild
                className="h-11 w-full justify-start rounded-full bg-white/10 text-stone-100 hover:bg-white/20"
                data-testid="consultation-email-button"
              >
                <a href={`mailto:${contactDetails.email}`} aria-label="Email event organizer">
                  <Mail className="h-4 w-4" />
                  {contactDetails.email}
                </a>
              </Button>
              <Button
                asChild
                className="h-11 w-full justify-start rounded-full bg-white/10 text-stone-100 hover:bg-white/20"
                data-testid="consultation-linkedin-button"
              >
                <a href={contactDetails.linkedin} target="_blank" rel="noreferrer" aria-label="Visit LinkedIn profile">
                  <Linkedin className="h-4 w-4" />
                  LinkedIn Profile
                </a>
              </Button>
            </div>

            <p data-testid="consultation-response-time" className="text-xs uppercase tracking-[0.15em] text-fuchsia-200">
              Typical response time: under 24 hours
            </p>
          </CardContent>
        </Card>

        <Card data-testid="consultation-form-card" className="border-fuchsia-200 bg-violet-50/75">
          <CardContent className="p-7">
            <form onSubmit={handleSubmit} className="grid gap-4" data-testid="consultation-form">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="full_name" data-testid="consultation-full-name-label">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    aria-label="Full name"
                    data-testid="consultation-full-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" data-testid="consultation-email-label">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    aria-label="Email address"
                    data-testid="consultation-email-input"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone" data-testid="consultation-phone-label">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    aria-label="Phone number"
                    data-testid="consultation-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_type" data-testid="consultation-event-type-label">Event Type</Label>
                  <Input
                    id="event_type"
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleChange}
                    placeholder="Conference / Summit / Product Launch"
                    required
                    aria-label="Event type"
                    data-testid="consultation-event-type-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="primary_market" data-testid="consultation-primary-market-label">Primary Market / Location</Label>
                <Input
                  id="primary_market"
                  name="primary_market"
                  value={formData.primary_market}
                  onChange={handleChange}
                  placeholder="Type city/country (e.g., Singapore, Mumbai, Sydney)"
                  aria-label="Primary market or location"
                  data-testid="consultation-primary-market-input"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="event_date" data-testid="consultation-event-date-label">Preferred Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        id="event_date"
                        data-testid="consultation-event-date-picker-button"
                        aria-label="Choose preferred event date"
                        className="h-10 w-full justify-start border-fuchsia-200 px-3 text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-fuchsia-700" />
                        {selectedEventDate ? format(selectedEventDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" data-testid="consultation-event-date-picker-content">
                      <Calendar
                        mode="single"
                        selected={selectedEventDate}
                        onSelect={handleDateSelect}
                        disabled={(dateValue) => dateValue < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                        data-testid="consultation-event-date-picker-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget" data-testid="consultation-budget-label">Estimated Investment (Optional)</Label>
                  <Input
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="Optional"
                    aria-label="Budget range"
                    data-testid="consultation-budget-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guest_count" data-testid="consultation-guest-count-label">Expected Delegates</Label>
                  <Input
                    id="guest_count"
                    name="guest_count"
                    type="number"
                    min="1"
                    value={formData.guest_count}
                    onChange={handleChange}
                    placeholder="120"
                    aria-label="Estimated number of guests"
                    data-testid="consultation-guest-count-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferred_contact" data-testid="consultation-preferred-contact-label">Preferred Contact Method</Label>
                <select
                  id="preferred_contact"
                  name="preferred_contact"
                  value={formData.preferred_contact}
                  onChange={handleChange}
                  aria-label="Preferred contact method"
                  data-testid="consultation-preferred-contact-select"
                  className="h-10 w-full rounded-md border border-fuchsia-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40"
                >
                  <option value="Email">Email</option>
                  <option value="Phone">Phone</option>
                  <option value="WhatsApp">WhatsApp</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" data-testid="consultation-message-label">Tell us about your event</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="min-h-28"
                  placeholder="Share goals, audience size, speaker/emcee needs, marketing support, and timeline."
                  aria-label="Event details"
                  data-testid="consultation-message-input"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-11 rounded-full bg-fuchsia-700 px-7 text-xs font-semibold uppercase tracking-wider text-white hover:bg-fuchsia-800"
                  data-testid="consultation-form-submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Book Corporate Consultation"}
                </Button>
                <Button
                  type="button"
                  asChild
                  variant="outline"
                  className="h-11 rounded-full border-stone-300 px-7 text-xs font-semibold uppercase tracking-wider"
                  data-testid="consultation-calendly-link-button"
                >
                  <a href={contactDetails.calendly} target="_blank" rel="noreferrer" aria-label="Book on Calendly">
                    Book Corporate Call
                  </a>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ConsultationSection;